import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import {
  email as emailValidator,
  maxLength,
  minLength,
  required,
  requiredIf,
  sameAs,
  useVuelidate,
} from '@/plugins'
import { defineLocalStore } from '@/utils/localStore'
import { notifyValidation } from '@/utils/helpers'
import { confirmResetPassword, resetPasswordSendSms } from '@/requests/auth'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { routeHistory } from 'best-modules/utils'

type LoginCredentials = {
  email: string
  password: string
}

type ResetPasswordCredentials = {
  password: string | null
  passwordRepeat: string | null
  code: string | number | null
}

export const { initLocalStore: initAuth, useLocalStore: useAuth } =
  defineLocalStore('auth', () => {
    const store = useStore()
    const router = useRouter()

    const credentials = reactive<LoginCredentials>({
      email: '',
      password: '',
    })
    const resetData = reactive<ResetPasswordCredentials>({
      password: null,
      passwordRepeat: null,
      code: null,
    })
    const phone = ref('')
    const code = ref('')
    const isSendSms = ref(false)
    const qrCode = computed<string | null>(() => store.state.auth.qrCode)

    const loginRules = computed(() => {
      return {
        email: { required, email: emailValidator },
        password: { required },
      }
    })
    const confirmRules = {
      code: { required, minLength: minLength(6), maxLength: maxLength(6) },
    }
    const resetRules = {
      phone: { required },
      resetData: {
        password: {
          requiredIf: requiredIf(isSendSms),
        },
        passwordRepeat: {
          sameAs: sameAs(computed(() => resetData.password)),
        },
        code: { requiredIf: requiredIf(isSendSms) },
      },
    }

    const loginV$ = useVuelidate(loginRules, credentials)
    const confirmV$ = useVuelidate(confirmRules, { code })
    const resetV$ = useVuelidate(resetRules, { resetData, phone })

    const login = () => {
      loginV$.value.$validate()
      if (loginV$.value.$error) {
        return notifyValidation(loginV$.value)
      }

      return handleAsync('loginFirst', () => {
        return store
          .dispatch('loginFirst', {
            ...credentials,
            email: credentials.email.trim().toLowerCase(),
          })
          .then(res => {
            router.push({ name: 'auth-confirm' })
            return res
          })
      })
    }

    const backToLogin = () => {
      store.commit('removeAuthUser')
      router.push({ name: 'login' })
    }

    const confirmAuth = () => {
      confirmV$.value.$validate()
      if (confirmV$.value.$error) {
        return notifyValidation(confirmV$.value)
      }

      return handleAsync('confirmAuth', () => {
        return store.dispatch('loginSecond', code.value).then(async () => {
          await store.dispatch('user/getUserData')

          if (routeHistory.length > 2) {
            // @ts-ignore
            return router.push(routeHistory.at(-3))
          }

          return router.push({ name: 'home' })
        })
      })
    }

    const submitResetPassword = () => {
      resetV$.value.$validate()
      if (resetV$.value.$error) {
        return notifyValidation(resetV$.value)
      }

      return isSendSms.value ? resetPassword() : sendSms()
    }

    const sendSms = () => {
      return resetPasswordSendSms(phone.value).then(res => {
        if (res) {
          isSendSms.value = true
          resetV$.value.$reset()
        }
      })
    }

    const resetPassword = () => {
      return confirmResetPassword({
        password: resetData.password as string,
        code: resetData.code?.toString() as string,
      }).then(() => {
        setSnackbar({ text: 'Пароль успішно змінено' })
        router.push({ name: 'login' })
      })
    }

    return {
      credentials,
      resetData,
      phone,
      code,
      isSendSms,
      qrCode,
      loginV$,
      confirmV$,
      resetV$,
      login,
      backToLogin,
      confirmAuth,
      submitResetPassword,
    }
  })
